import React from 'react';
import { cn } from '../../design-system/cn';
import { Card } from '../ui';
import {
  formatFeeRateLabel,
  formatPercent,
  formatPayoutDollars,
  FEE_ESTIMATE_DISCLAIMER,
  isServerSettlement,
  resolvePayoutBreakdown,
} from '../../utils/platformFee';
import { typography } from '../../design-system/tokens';

/**
 * Standard payout breakdown — total pot, platform fee, winner receives.
 * @param {object} [settlement] — precomputed { totalPot, platformFeeRate, platformFeeAmount, winnerPayoutAmount }
 * @param {number} [stakePerPlayer] — optional row for confirmation tickets
 */
export default function PayoutReceipt({
  totalPot,
  stakePerPlayer,
  subscriptionProfile,
  settlement,
  compact = false,
  className = '',
  note,
  isEstimate: isEstimateProp,
}) {
  const fromServer = isServerSettlement(settlement);
  const isEstimate = isEstimateProp ?? !fromServer;
  const breakdown = resolvePayoutBreakdown({ settlement, totalPot, subscriptionProfile });
  const feeLabel = breakdown.platformFeeRate != null
    ? formatFeeRateLabel(breakdown.platformFeeRate)
    : '—';

  const rowClass = compact ? 'text-xs' : 'text-sm';
  const estimateNote = note || (isEstimate ? FEE_ESTIMATE_DISCLAIMER : null);

  return (
    <Card
      variant="raised"
      padding="sm"
      className={cn('space-y-2 text-left min-w-0 overflow-hidden sm:p-4', className)}
    >
      {isEstimate && (
        <p className={`${typography.microWide} text-amber-400/90`}>Estimated winner transfer</p>
      )}
      {stakePerPlayer != null && (
        <div className={`flex justify-between gap-3 ${rowClass}`}>
          <span className="text-gray-500 font-bold">Your entry</span>
          <span className="text-white font-black tabular-nums">${Number(stakePerPlayer).toFixed(2)}</span>
        </div>
      )}
      {stakePerPlayer != null && (
        <div className={`flex justify-between gap-3 ${rowClass}`}>
          <span className="text-gray-500 font-bold">Opponent entry</span>
          <span className="text-white font-black tabular-nums">${Number(stakePerPlayer).toFixed(2)}</span>
        </div>
      )}
      <div className={`flex justify-between gap-2 min-w-0 ${rowClass}`}>
        <span className="text-gray-500 font-bold shrink-0">Challenge pool</span>
        <span className="text-white font-black tabular-nums text-right min-w-0">
          {formatPayoutDollars(breakdown.totalPot)}
        </span>
      </div>
      <div className={`flex justify-between gap-2 min-w-0 ${rowClass}`}>
        <span className="text-gray-500 font-bold shrink-0">Platform fee ({feeLabel})</span>
        <span className="text-amber-300/90 font-black tabular-nums text-right min-w-0 break-words">
          {formatPercent(breakdown.platformFeeRate)} / {formatPayoutDollars(breakdown.platformFeeAmount)}
        </span>
      </div>
      <div className={`flex justify-between gap-2 pt-2 border-t border-gray-800 min-w-0 ${rowClass}`}>
        <span className="text-emerald-400 font-black uppercase tracking-wide shrink-0">Winner receives</span>
        <span className="text-emerald-400 font-black tabular-nums text-base sm:text-lg text-right min-w-0">
          {formatPayoutDollars(breakdown.winnerPayoutAmount)}
        </span>
      </div>
      {fromServer && breakdown.subscriptionDiscountApplied && (
        <p className={`${typography.microWide} text-emerald-400/90`}>LockedIn Plus rate applied at settlement</p>
      )}
      {estimateNote && (
        <p className="text-xs text-gray-500 leading-relaxed font-medium">{estimateNote}</p>
      )}
    </Card>
  );
}
